/* Maatcoach: personal size advice on every product page. Loaded by snippets/maatcoach.liquid, no dependencies.

   The shopper answers once: height, weight and build (or her own measurements), and for shoes her usual size. The
   answers stay in this browser (localStorage, nothing is sent anywhere), and from then on every product page names
   her size under the sizes and marks it, with the reasoning at the top of the size guide pop-up.

   Two halves in this file:
   - the engine: pure functions, no DOM. It reads the product's size chart (custom.size_chart: columns plus rows),
     repairs what supplier charts get wrong and advises one size. scripts/shopify/maatcoach.mjs runs this same code in
     Node over every product of a store, so the check sees exactly what the shopper gets;
   - the page: the form and the answer inside the pop-up, and the line under the sizes.

   How the advice is made (sources and formulas: google-stores/store-15/research/taglie-en-maatadvies-2026-09-27.md):
   1. Her body. Hips from height and weight (Hip Index exponents, 103 cm at 73 kg and 166 cm), waist and bust from the
      hips along the German size table; the build moves all three. Her own measurements replace the estimate. The
      same model as the jeans advice in assets/size-guide.js.
   2. What this model intends. For every size of the chart: garment measurement minus the standard body of that size
      label. The median is the ease the model is cut with: a wrap coat cut oversize has +43 cm at the bust, a ribbed
      turtleneck -18 cm. She gets the size where her body plus that same ease meets the chart, so an oversize coat
      still comes out oversize on her and a stretch knit still hugs.
   3. One size, always. Between two sizes the kind of garment decides (the larger, for a jumper underneath or more
      room; an ample cut the smaller) and the other size is offered next to it.
   Shoes: her usual size, one up when this model's foot length runs half a size short, never one down on the chart's
   word alone; with her foot length, the smallest size whose foot length takes it. Sizes in EU or in US women's sizes
   (thema 2: the form asks in US sizes in the United States and Canada).

   Repairs, each reported by the check script: waist and hips swapped (a trouser waist wider than its hips), flat
   widths instead of circumferences, measurements that shrink as the size grows, a foot column that is really an
   outsole. A column that cannot be trusted is dropped; with no column left, the advice falls back to her usual size
   on the standard table, and says so. */
(function (root) {
  'use strict';

  /* ======================================================================================================== */
  /* Engine                                                                                                   */
  /* ======================================================================================================== */
  var MC = { version: 1 };

  /* Standard body per size label, cm: bust, waist, hips. XXS to 5XL are the German sizes 32 to 50: waist and hips
     are the midpoints of the German size table, the bust is 2 x (size + 6). */
  var LETTERS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
  var STD = [
    [76, 59.5, 83], [80, 63, 87], [84, 66.5, 92], [88, 70.5, 96], [92, 75, 99.5],
    [96, 79.5, 103], [100, 83.5, 106.5], [104, 88, 110.5], [108, 93, 114.5], [112, 99, 119.5]
  ];
  var AXIS = { chest: 0, waist: 1, hip: 2 };

  /* Bust 1.067 x hips - 14.1 is the German table read the same way (bust against hips, within 1.5 cm from size 34 to
     48). The build moves bust, waist and hips by the same centimetres, as in the jeans advice. */
  var BODY = {
    hip: function (h, w) { return 103 * Math.pow(w / 73, 0.482) * Math.pow(h / 166, -0.31); },
    waist: function (hip) { return 1.1 * hip - 33.5; },
    chest: function (hip) { return 1.067 * hip - 14.1; },
    build: { slim: -2, mid: 0, full: 2.5 }
  };

  /* Men (thema 2, 08-10-2026, owner: "bij de mannenstore noctalume een vrouw ... dat hier de postuur staat van een man").
     Standard body per letter from the German men's sizes 44 to 62 (chest = 2 x size; S = 46, M = 48/50, L = 52/54,
     XL = 56/58, XXL = 60/62, waist and hips from the same table). His body: chest from height and weight by geometric
     scaling (a girth grows with the square root of weight over height), 100 cm at 80 kg and 178 cm; waist and hips
     follow the chest along the same table. The build moves all three, as for women. */
  var STD_MEN = [
    [84, 72, 88], [88, 76, 92], [92, 80, 96], [98, 86, 100], [106, 94, 106],
    [114, 102, 112], [122, 110, 118], [128, 117, 124], [134, 124, 130], [140, 131, 136]
  ];
  var BODY_MEN = {
    chest: function (h, w) { return 100 * Math.pow(w / 80, 0.5) * Math.pow(h / 178, -0.5); },
    waist: function (chest) { return 1.25 * chest - 39; },
    hip: function (chest) { return 0.6 * chest + 42; },
    build: { slim: -2.5, mid: 0, full: 3 }
  };
  var STD_WOMEN = STD;
  MC.kind = 'women';
  /* Which body the advice uses: 'men' or 'women' (the default). The page sets it from data-mc-body. */
  MC.setBody = function (kind) {
    MC.kind = kind === 'men' ? 'men' : 'women';
    STD = MC.kind === 'men' ? STD_MEN : STD_WOMEN;
  };

  /* A design ease outside these bounds is a broken chart, not a design. When the size labels say nothing about the
     body (no letters), the rule-of-thumb ease below stands in and the check script says so. */
  var EASE_RANGE = { chest: [-25, 50], waist: [-20, 60], hip: [-25, 45] };
  var EASE_DEFAULT = { chest: 12, waist: 4, hip: 6 };

  /* Shoe sizes in two systems: EU, in whole sizes, and US women's, in half sizes
     ("US 6-6.5", "US7"; thema 2, 03-10-2026, for the American stores). Per system the foot length most of our shoe
     charts give: EU 35: 22.5 cm, 38: 24 cm, 40: 25 cm; US 6: 23 cm, 8: 24.6 cm, 11: 27 cm (measured on the US charts of
     Mirevalia and Elowenne). A model whose own column sits 0.4 cm or more below it at her size runs small. The same
     foot length carries her size over to the other system when she answered in that one. her: the usual sizes the
     form accepts. */
  var SHOE = {
    eu: { lo: 30, hi: 50, her: [30, 48], whole: true, foot: function (x) { return 22.5 + 0.5 * (x - 35); }, size: function (f) { return 35 + (f - 22.5) / 0.5; } },
    us: { lo: 3, hi: 14, her: [3, 14], whole: false, foot: function (x) { return 23 + 0.8 * (x - 6); }, size: function (f) { return 6 + (f - 23) / 0.8; } }
  };
  function shoeConvert(v, from, to) {
    if (from === to) return v;
    return Math.round(SHOE[to].size(SHOE[from].foot(v)) * 2) / 2;
  }

  /* Words, in the store languages we sell in: French, English, Dutch, German, Italian, Polish, Danish, Spanish. */
  var RX = {
    one: /taille unique|one ?size|onesize|one-size|taglia unica|einheitsgr|uniwersaln|unica|universal|\bunique\b|one fits/i,
    shoe: /chaussure|botte|bottine|basket|sandale|escarpin|mocassin|chausson|pantoufle|\bmules?\b|sneaker|\bboots?\b|\bshoes?\b|sandal|slipper|loafer|\bpumps?\b|schoen|laars|laarzen|pantoffel|slof|schuh|stiefel|scarpe|scarpa|stival|pantofol|\bbuty\b|kozaki|botki|kapcie|\bsko\b|støvle|tøffel|zapato|\bbotas?\b|zapatilla/,
    set: /ensemble|\bsets?\b|co-?ord|pyjama|pajama|twin-?set|completo|zestaw|\bsæt\b|conjunto|tailleur/,
    bottom: /pantalon|legging|jogging|jogger|\bjeans?\b|jupe|\bshorts?\b|bermuda|trouser|\bpants\b|skirt|broek|\brok\b|\bhose\b|\brock\b|pantaloni|gonna|spodnie|spódnic|bukser|nederdel|falda|culotte/,
    dress: /\brobe|dress|jurk|kleid|abito|vestito|sukienk|kjole|vestido|combinaison|jumpsuit|overall|salopette/,
    outer: /manteau|veste|doudoune|parka|blouson|\bcape\b|trench|caban|\bcoat|jacket|\bjas\b|\bmantel|jacke|giacca|cappotto|piumino|kurtka|płaszcz|frakke|jakke|abrigo|chaqueta|anorak|bodywarmer/,
    knit: /\bpull|gilet|cardigan|sweat|maille|tricot|\btrui|\bvest\b|pullover|strick|maglion|maglia|sweter|\bstrik|jersey|jumper|\bknit/,
    ample: /oversize|\bample|cocooning|chauve-souris|batwing|\bloose|ruimvallend|weit geschnitten|ampio|luźn|\bløs/,
    wide: /large|wide|ample|palazzo|évasé|evase|flare|baggy|jogging|jogger|wijd|weit|ampi|szerok|\bvid/,
    narrow: /droit|straight|slim|legging|skinny|cigarette|recht|gerade|dritt|prost|\blige/
  };

  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    var s = String(v == null ? '' : v).replace(/,/g, '.');
    var m = s.match(/\d+(?:\.\d+)?/g);
    if (!m) return null;
    var a = parseFloat(m[0]);
    /* "100 to 104", "100-104": the middle. */
    if (m.length > 1 && /\d\s*(?:-|–|—|~|to|à|bis|tot|do|til)\s*\d/i.test(s)) return (a + parseFloat(m[1])) / 2;
    return a;
  }

  function median(list) {
    if (!list.length) return null;
    var s = list.slice().sort(function (x, y) { return x - y; });
    var m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }

  function sizeKey(s) {
    var k = String(s == null ? '' : s).toUpperCase().replace(/\s+/g, '');
    if (k === '2XL') return 'XXL';
    if (k === 'XXXL') return '3XL';
    if (k === 'XXXXL') return '4XL';
    if (k === 'XXXXXL') return '5XL';
    return k;
  }

  function letterIndex(s) {
    var k = sizeKey(s);
    var i = LETTERS.indexOf(k);
    if (i > -1) return i;
    if (/^\d{2}$/.test(k)) {
      var n = parseInt(k, 10);
      if (n >= 32 && n <= 50 && n % 2 === 0) return (n - 32) / 2;
    }
    return -1;
  }

  /* The standard body for a size label; "S/M" sits halfway. Null for labels without a body (a shoe size, "One size"). */
  function stdOf(size) {
    var parts = String(size).split('/');
    var sum = 0;
    for (var i = 0; i < parts.length; i++) {
      var li = letterIndex(parts[i]);
      if (li < 0) return null;
      sum += li;
    }
    var x = sum / parts.length;
    var lo = Math.floor(x);
    var hi = Math.ceil(x);
    var f = x - lo;
    return [0, 1, 2].map(function (a) { return STD[lo][a] + f * (STD[hi][a] - STD[lo][a]); });
  }

  function shoeRange(s) {
    var t = String(s).replace(/,/g, '.');
    var sys = 'us';
    var m = t.match(/\bUS\s*(\d{1,2}(?:\.5)?)(?:\s*[-\u2013\/]\s*(?:US\s*)?(\d{1,2}(?:\.5)?))?/i);
    if (!m) {
      sys = 'eu';
      m = t.match(/(\d{2}(?:\.5)?)(?:\s*[-\u2013\/]\s*(\d{2}(?:\.5)?))?/);
    }
    if (!m) return null;
    var lo = parseFloat(m[1]);
    var hi = m[2] ? parseFloat(m[2]) : lo;
    if (lo < SHOE[sys].lo || hi > SHOE[sys].hi || hi < lo) return null;
    return { sys: sys, lo: lo, hi: hi, c: (lo + hi) / 2 };
  }

  /* What a chart column measures. A set prefixes its columns with the piece ("Hauts", "Pantalons"). Foot girth and
     shaft circumference are not the foot length. */
  function colDim(label) {
    var s = String(label || '').toLowerCase();
    var part = null;
    if (/^(hauts?|tops?|bovenstuk|oberteil|parte superiore|góra|overdel)\b/.test(s)) part = 'top';
    else if (/^(pantalons?|bas|bottoms?|jupes?|shorts?|broek|hose|pantaloni|gonna|spodnie|bukser|underdel|dół)\b/.test(s)) part = 'bottom';
    var dim = null;
    if (/tour de pied|girth|omtrek|umfang des fu|ballen|circonferenza del piede|obwód stopy|tour de tige|mollet|calf|kuit|wade|polpaccio|łydk|læg/.test(s)) dim = null;
    else if (/poitrine|chest|bust|borst|brust|oberweite|petto|seno|torace|klatk|biust|bryst|pecho|busto/.test(s)) dim = 'chest';
    else if (/hanche|\bhips?\b|heup|hüft|huft|fianch|bacino|biodr|hofte|cadera/.test(s)) dim = 'hip';
    else if (/tour de taille|waist|taillen|^taille|\btaille \(|\bvita\b|talia|talje|cintura|ceinture|waistband|tailleband/.test(s)) dim = 'waist';
    else if (/entrejambe|inseam|binnenbeen|innenbein|schritt|cavallo|interno|wewnętrzn|indvendig|entrepierna/.test(s)) dim = 'inseam';
    else if (/\bpied\b|\bfoot\b|voet|fuß|fuss|piede|stopa|stopy|\bfod\b|\bpie\b|semelle|insole|binnenzool|innensohle|soletta|wkładk|indersål|plantilla/.test(s)) dim = 'foot';
    return { dim: dim, part: part };
  }

  function readChart(chart) {
    var out = { rows: [], measure: 'garment', cols: [] };
    if (!chart || !chart.rows || !chart.columns) return out;
    var factor = /^in/i.test(String(chart.unit || 'cm')) ? 2.54 : 1;
    out.measure = String(chart.measure || 'garment').toLowerCase() === 'body' ? 'body' : 'garment';
    for (var c = 0; c < chart.columns.length; c++) out.cols.push(colDim(chart.columns[c]));
    for (var r = 0; r < chart.rows.length; r++) {
      var row = chart.rows[r];
      if (!row || row.size == null) continue;
      var v = {};
      var cells = row.values || [];
      for (var k = 0; k < out.cols.length; k++) {
        var cd = out.cols[k];
        if (!cd.dim || v[cd.dim] != null) continue;
        if (cd.part === 'bottom' && cd.dim === 'chest') continue;
        if (cd.part === 'top' && (cd.dim === 'waist' || cd.dim === 'hip' || cd.dim === 'inseam')) continue;
        var n = num(cells[k]);
        if (n != null && n > 0) v[cd.dim] = n * factor;
      }
      out.rows.push({ size: String(row.size), key: sizeKey(row.size), std: stdOf(row.size), v: v });
    }
    return out;
  }

  /* Waist and hips swapped: a trouser waist is never wider than its hips, so when most rows say so the two columns
     were labelled the wrong way round (Sovelyne, wide-leg knit trousers: "waist" 114, "hips" 71 in XXS). */
  function fixSwap(rows, notes) {
    var both = 0;
    var flipped = 0;
    rows.forEach(function (r) {
      if (r.v.waist != null && r.v.hip != null) {
        both++;
        if (r.v.waist > r.v.hip + 1) flipped++;
      }
    });
    if (both && flipped * 2 > both) {
      rows.forEach(function (r) {
        var w = r.v.waist;
        r.v.waist = r.v.hip;
        r.v.hip = w;
      });
      notes.push('swap');
    }
  }

  /* One circumference over the given rows: its values (doubled when the chart gives flat widths), the design ease and
     the grading step. Null when the column cannot be trusted. */
  function prepDim(all, rows, d, measure, notes, quiet) {
    var list = rows.filter(function (r) { return r.v[d] != null; });
    if (list.length < 2) return null;
    var raw = list.map(function (r) { return r.v[d]; });
    for (var i = 0; i < raw.length - 1; i++) {
      if (raw[i + 1] < raw[i] - 1) {
        if (!quiet) notes.push(d + ':order');
        return null;
      }
    }
    var half = measure !== 'body' && median(raw) < (d === 'waist' ? 45 : 60);
    var factor = half ? 2 : 1;
    var vals = raw.map(function (x) { return x * factor; });
    var T = 0;
    if (measure !== 'body') {
      /* A pattern is drafted at a base size and graded from there, and our suppliers grade L and up with extra room
         (+4 cm per size up to M, then +6). So the ease the model is designed with comes from the sizes up to M; the
         larger sizes keep their extra room, which the chart then shows. */
      var diffs = [];
      var core = [];
      all.forEach(function (r) {
        if (r.v[d] == null || !r.std) return;
        var e = r.v[d] * factor - r.std[AXIS[d]];
        diffs.push(e);
        if (r.std[0] <= STD[3][0]) core.push(e);
      });
      if (core.length >= 2) {
        T = median(core);
      } else if (diffs.length >= 2) {
        T = median(diffs);
      } else {
        T = EASE_DEFAULT[d];
        if (!quiet) notes.push(d + ':default');
      }
      if (T < EASE_RANGE[d][0] || T > EASE_RANGE[d][1]) {
        if (!quiet) notes.push(d + ':implausible');
        return null;
      }
      if (half && !quiet) notes.push(d + ':half');
    }
    var steps = [];
    for (var j = 0; j < vals.length - 1; j++) if (vals[j + 1] - vals[j] > 0.5) steps.push(vals[j + 1] - vals[j]);
    return { dim: d, sizes: list.map(function (r) { return r.size; }), vals: vals, T: T, step: median(steps) || 4, rows: list };
  }

  function prepShoe(sell, notes) {
    var rows = [];
    var sys = null;
    sell.forEach(function (r) {
      var rg = shoeRange(r.size);
      if (!rg) return;
      /* One system per model: a label in the other one is left out, and reported. */
      if (sys && rg.sys !== sys) {
        if (notes.indexOf('shoe:mixed') < 0) notes.push('shoe:mixed');
        return;
      }
      sys = rg.sys;
      rows.push({ size: r.size, lo: rg.lo, hi: rg.hi, c: rg.c, foot: r.v.foot != null ? r.v.foot : null });
    });
    sys = sys || 'eu';
    var withFoot = rows.filter(function (x) { return x.foot != null; });
    var ok = withFoot.length >= 2;
    if (ok) {
      for (var i = 0; i < withFoot.length - 1; i++) {
        if (withFoot[i + 1].foot < withFoot[i].foot - 0.05) { ok = false; notes.push('foot:order'); break; }
      }
    }
    if (ok && withFoot.some(function (x) { return x.foot < 19 || x.foot > 32; })) { ok = false; notes.push('foot:range'); }
    var dev = ok ? median(withFoot.map(function (x) { return x.foot - SHOE[sys].foot(x.c); })) : null;
    if (ok && Math.abs(dev) > 1.5) { ok = false; notes.push('foot:scale'); }
    return { rows: rows, footOk: ok, dev: dev, sys: sys };
  }

  function kindOf(text, has) {
    if (RX.shoe.test(text)) return 'shoe';
    if (RX.set.test(text)) return 'set';
    if (RX.bottom.test(text)) return 'bottom';
    if (RX.dress.test(text)) return 'dress';
    if (has.foot && !has.chest && !has.hip) return 'shoe';
    if (has.chest) return 'top';
    if (has.hip || has.waist) return 'bottom';
    return 'top';
  }

  function subOf(title, type) {
    if (RX.outer.test(title)) return 'outer';
    if (RX.knit.test(title)) return 'knit';
    if (RX.outer.test(type)) return 'outer';
    if (RX.knit.test(type)) return 'knit';
    return 'top';
  }

  function cutOf(pd, sub, title, kind) {
    if (!pd) return null;
    var T = pd.T;
    var cut;
    if (pd.dim === 'chest') {
      var t = sub === 'outer' ? [12, 24] : [8, 18];
      cut = T < 0 ? 'near' : T < t[0] ? 'fitted' : T < t[1] ? 'regular' : 'ample';
      /* The title's word for the cut wins over the numbers, unless the numbers say it hugs. */
      if (cut !== 'near' && (kind === 'top' || kind === 'dress') && RX.ample.test(title)) cut = 'ample';
    } else if (pd.dim === 'hip') {
      cut = T < -3 ? 'near' : T < 4 ? 'fitted' : T < 10 ? 'regular' : 'ample';
    } else {
      cut = T < 0 ? 'near' : T < 6 ? 'fitted' : 'regular';
    }
    return cut;
  }

  /* Everything that does not depend on the shopper: which kind of product, which measurements decide, how it is cut.
     prod: { title, type, sizes: [option values in order], chart: custom.size_chart value }. */
  MC.analyse = function (prod) {
    var sizes = (prod && prod.sizes ? prod.sizes : []).map(String);
    var title = String(prod && prod.title || '').toLowerCase();
    var type = String(prod && prod.type || '').toLowerCase();
    var a = { kind: 'one', sub: null, dims: [], notes: [], sizes: sizes, can: false, cut: null, std: null, shoe: null, inseam: null };
    if (sizes.length < 2 || sizes.every(function (s) { return RX.one.test(s); })) return a;
    var chart = readChart(prod.chart);
    a.measure = chart.measure;
    var byKey = {};
    chart.rows.forEach(function (r) { if (!byKey[r.key]) byKey[r.key] = r; });
    var sell = sizes.map(function (s) {
      var r = byKey[sizeKey(s)];
      return { size: s, std: stdOf(s), v: r ? r.v : {} };
    });
    var has = {};
    chart.rows.forEach(function (r) { Object.keys(r.v).forEach(function (k) { has[k] = true; }); });
    a.kind = kindOf(type + ' ' + title, has);
    if (a.kind === 'shoe') {
      a.shoe = prepShoe(sell, a.notes);
      a.can = a.shoe.rows.length >= 2;
      return a;
    }
    if (has.waist && has.hip) fixSwap(chart.rows, a.notes);
    a.sub = a.kind === 'top' ? subOf(title, type) : null;
    var want = { top: ['chest'], bottom: ['hip', 'waist'], dress: ['chest', 'waist', 'hip'], set: ['chest', 'hip', 'waist'] }[a.kind];
    var prepared = {};
    want.forEach(function (d) { prepared[d] = prepDim(chart.rows, sell, d, chart.measure, a.notes); });
    var pick;
    if (a.kind === 'top') pick = [prepared.chest];
    else if (a.kind === 'bottom') pick = [prepared.hip || prepared.waist];
    else if (a.kind === 'dress') pick = [prepared.chest, prepared.waist, prepared.hip];
    else pick = [prepared.chest, prepared.hip || prepared.waist];
    pick = pick.filter(Boolean);
    /* Several measurements decide together only over the sizes that carry all of them. */
    if (pick.length > 1) {
      var common = sell.filter(function (r) { return pick.every(function (pd) { return r.v[pd.dim] != null; }); });
      pick = pick.map(function (pd) { return prepDim(chart.rows, common, pd.dim, chart.measure, a.notes, true); }).filter(Boolean);
    }
    a.dims = pick;
    var lettered = sell.filter(function (r) { return r.std; });
    a.std = lettered.length >= 2 ? lettered : null;
    if (sell.some(function (r) { return r.v.inseam != null; })) a.inseam = sell;
    a.cut = cutOf(pick[0], a.sub, title, a.kind);
    a.wide = RX.wide.test(title) ? 5 : RX.narrow.test(title) ? 2 : 3;
    a.can = pick.length > 0 || !!a.std;
    return a;
  };

  /* Her body in cm (chest, waist, hip), or null. p: { h, w, b, chest, waist, hip }. */
  MC.body = function (p) {
    if (!p) return null;
    var est = null;
    if (p.h >= 130 && p.h <= 210 && p.w >= 30 && p.w <= 200) {
      if (MC.kind === 'men') {
        var c = BODY_MEN.chest(p.h, p.w);
        var sm = BODY_MEN.build[p.b] || 0;
        est = { chest: c + sm, waist: BODY_MEN.waist(c) + sm, hip: BODY_MEN.hip(c) + sm };
      } else {
        var hip = BODY.hip(p.h, p.w);
        var s = BODY.build[p.b] || 0;
        est = { chest: BODY.chest(hip) + s, waist: BODY.waist(hip) + s, hip: hip + s };
      }
    }
    var out = { own: {}, est: !!est };
    var any = false;
    ['chest', 'waist', 'hip'].forEach(function (d) {
      var v = Number(p[d]);
      if (v >= 50 && v <= 200) {
        out[d] = v;
        out.own[d] = true;
        any = true;
      } else if (est) {
        out[d] = est[d];
        any = true;
      }
    });
    return any ? out : null;
  };

  /* Where her body plus the design ease falls on the chart, as a position in size steps: 2 is exactly the third size,
     2.5 halfway to the fourth. Beyond the ends it goes on with the grading step. */
  function place(vals, step, t) {
    var n = vals.length;
    if (t <= vals[0]) return -(vals[0] - t) / step;
    if (t >= vals[n - 1]) return (n - 1) + (t - vals[n - 1]) / step;
    for (var i = 0; i < n - 1; i++) {
      if (t <= vals[i + 1]) {
        var d = vals[i + 1] - vals[i];
        return d > 0 ? i + (t - vals[i]) / d : i;
      }
    }
    return n - 1;
  }

  /* One size from a position, and two when she sits in the middle third between them. */
  function decide(x, n) {
    if (x > n - 1 + 1) return { out: true, i: n - 1 };
    if (x > n - 1 + 0.5) return { i: n - 1, snug: true };
    if (x < -1) return { i: 0, roomy: true };
    if (x <= 0) return { i: 0 };
    var lo = Math.floor(x);
    if (lo >= n - 1) return { i: n - 1 };
    var f = x - lo;
    if (f < 0.3) return { i: lo };
    if (f > 0.7) return { i: lo + 1 };
    return { between: true, lo: lo, hi: lo + 1 };
  }

  function adviseClothes(a, p, res) {
    var body = MC.body(p);
    if (!body) return res;
    var list;
    var sizes;
    if (a.dims.length) {
      res.basis = 'chart';
      sizes = a.dims[0].sizes;
      list = a.dims.map(function (pd) {
        var b = body[pd.dim];
        return b == null ? null : { pd: pd, b: b, x: place(pd.vals, pd.step, b + pd.T) };
      });
    } else {
      /* No measurement to trust: her usual size on the standard table. */
      res.basis = 'standard';
      sizes = a.std.map(function (r) { return r.size; });
      var axes = a.kind === 'bottom' ? ['hip'] : a.kind === 'top' ? ['chest'] : ['chest', 'hip'];
      list = axes.map(function (d) {
        var vals = a.std.map(function (r) { return r.std[AXIS[d]]; });
        return body[d] == null ? null : { pd: { dim: d, vals: vals, T: 0, step: 4, sizes: sizes }, b: body[d], x: place(vals, 4, body[d]) };
      });
    }
    if (list.some(function (e) { return !e; })) return res;
    var main = list[0];
    list.forEach(function (e) { if (e.x > main.x) main = e; });
    var n = sizes.length;
    var dec = decide(main.x, n);
    res.status = dec.out ? 'out' : 'ok';
    res.own = !!body.own[main.pd.dim];
    res.dim = main.pd.dim;
    res.cut = a.cut;
    res.sub = a.sub;
    res.flags = { roomy: !!dec.roomy, snug: !!dec.snug };
    /* An ample top keeps its look one size down; trousers keep room at the hips and length, so they go up. */
    var prefer = a.kind === 'top' && a.cut === 'ample' ? 'down' : 'up';
    if (dec.between) {
      res.between = true;
      res.lower = sizes[dec.lo];
      res.upper = sizes[dec.hi];
      res.pick = prefer === 'down' ? res.lower : res.upper;
      res.alt = prefer === 'down' ? res.upper : res.lower;
      res.prefer = prefer;
    } else {
      res.pick = sizes[dec.i];
      res.alt = null;
    }
    var pi = sizes.indexOf(res.pick);
    res.ease = res.basis === 'chart' && a.measure !== 'body' ? Math.round(main.pd.vals[pi] - main.b) : null;
    res.garment = res.basis === 'chart' ? Math.round(main.pd.vals[pi]) : null;
    res.body = Math.round(main.b);
    /* A set or a dress where the pieces disagree: name both. */
    if (list.length > 1) {
      var picks = list.map(function (e) { return sizes[Math.max(0, Math.min(n - 1, Math.round(e.x)))]; });
      var differ = picks.some(function (s) { return s !== picks[0]; });
      if (differ) res.split = list.map(function (e, k) { return { dim: e.pd.dim, size: picks[k] }; });
    }
    /* Trousers: where the hem falls. Crotch height about 0.47 x height, the garment hangs a little below it. */
    if (a.inseam && p && p.h >= 130 && p.h <= 210) {
      var row = a.inseam.filter(function (r) { return r.size === res.pick; })[0];
      if (row && row.v.inseam) {
        var above = 0.47 * p.h - a.wide - row.v.inseam;
        res.hem = above <= 1 ? 'long' : above <= 9 ? 'ankle' : 'short';
      }
    }
    return res;
  }

  function adviseShoe(a, p, res) {
    var s = a.shoe;
    var st = SHOE[s.sys];
    var rows = s.rows;
    var n = rows.length;
    var foot = Number(p.foot) >= 18 && Number(p.foot) <= 32 ? Number(p.foot) : null;
    /* Her usual size, in the system she answered in (EU, unless the form asked her for US sizes); on a model in the
       other system it is carried over through the foot length. */
    var herSys = p.shoeSys === 'us' ? 'us' : 'eu';
    var own = Number(p.shoe) >= SHOE[herSys].her[0] && Number(p.shoe) <= SHOE[herSys].her[1] ? Number(p.shoe) : null;
    var usual = own == null ? null : shoeConvert(own, herSys, s.sys);
    if (foot == null && usual == null) return res;
    res.status = 'ok';
    res.flags = {};
    res.alt = null;
    if (foot != null && s.footOk) {
      /* Her foot against this model's foot length: the smallest size that takes it. */
      res.basis = 'foot';
      res.foot = foot;
      var i = -1;
      for (var k = 0; k < n; k++) {
        if (rows[k].foot != null && rows[k].foot >= foot - 0.1) { i = k; break; }
      }
      if (i === -1) {
        var last = rows[n - 1];
        if (last.foot != null && foot <= last.foot + 0.4) {
          res.pick = last.size;
          res.flags.snug = true;
        } else {
          res.status = 'out';
          res.pick = last.size;
        }
        return res;
      }
      res.pick = rows[i].size;
      res.col = rows[i].foot;
      /* Less than 0.3 cm to spare: fine barefoot or in thin socks, the next size up for thick ones. */
      if (rows[i].foot - foot < 0.3 && i < n - 1) {
        res.tight = true;
        res.alt = rows[i + 1].size;
      }
      return res;
    }
    if (usual == null) {
      /* Only her foot length, and a chart whose foot column cannot be read: the size that length usually is. */
      usual = Math.round(st.size(foot) * 2) / 2;
      res.basis = 'footStd';
      res.foot = foot;
    } else {
      res.basis = 'usual';
      res.given = { size: own, sys: herSys };
    }
    res.usual = usual;
    res.sys = s.sys;
    /* EU models come in whole sizes, so her half size takes the one up; US sizes have their halves. */
    var half = st.whole && usual % 1 !== 0;
    var want = half ? Math.ceil(usual) : usual;
    var idx = -1;
    for (var j = 0; j < n; j++) {
      if (want >= rows[j].lo && want <= rows[j].hi) { idx = j; break; }
    }
    if (idx === -1) {
      if (want < rows[0].lo) {
        res.pick = rows[0].size;
        res.flags.roomy = true;
        if (want < rows[0].lo - 1) res.status = 'out';
      } else if (want > rows[n - 1].hi) {
        res.pick = rows[n - 1].size;
        res.status = 'out';
      } else {
        /* Between two sizes of this model (US 7 where it has US 6.5 and US 7.5): the one up, for comfort, and the one
           below as the other choice. */
        for (var b = 1; b < n; b++) {
          if (rows[b].lo > want) {
            res.half = true;
            res.pick = rows[b].size;
            res.alt = rows[b - 1].size;
            break;
          }
        }
        if (!res.pick) {
          res.pick = rows[n - 1].size;
          res.status = 'out';
        }
      }
      return res;
    }
    res.half = half;
    if (s.footOk && rows[idx].foot != null && !half) {
      var d = rows[idx].foot - st.foot(usual);
      if (d <= -0.4 && idx < n - 1) {
        res.runs = 'small';
        res.pick = rows[idx + 1].size;
        res.alt = rows[idx].size;
        return res;
      }
      if (d >= 0.6) res.runs = 'large';
    }
    res.pick = rows[idx].size;
    if (half && idx > 0 && rows[idx - 1].hi < want) res.alt = rows[idx - 1].size;
    res.range = rows[idx].lo !== rows[idx].hi;
    return res;
  }

  /* The advice for this product and this shopper. status: 'ok', 'out' (beyond the largest size), 'need' (answers
     missing) or 'none' (nothing to advise on). */
  MC.advise = function (a, p) {
    var res = { status: 'need', kind: a.kind, pick: null, alt: null };
    if (!a.can) {
      res.status = 'none';
      return res;
    }
    if (a.kind === 'shoe') return adviseShoe(a, p || {}, res);
    return adviseClothes(a, p || {}, res);
  };

  /* Her size on the standard table for one measurement, as a letter; the check script compares the advice with it. */
  MC.standardSize = function (dim, value) {
    var axis = AXIS[dim];
    var best = 0;
    for (var i = 1; i < STD.length; i++) {
      if (Math.abs(STD[i][axis] - value) < Math.abs(STD[best][axis] - value)) best = i;
    }
    return LETTERS[best];
  };
  MC.letterIndex = letterIndex;
  MC.shoeConvert = shoeConvert;

  root.Maatcoach = MC;

  /* ======================================================================================================== */
  /* Page                                                                                                     */
  /* ======================================================================================================== */
  if (typeof document === 'undefined') return;

  var KEY = 'maatcoach-v1';

  function readProfile() {
    try {
      var p = JSON.parse(window.localStorage.getItem(KEY) || 'null');
      if (p && typeof p === 'object') return p;
    } catch (err) { /* storage blocked: the advice still works for this page */ }
    /* A store that also runs the jeans advice keeps her answers under its own key: take them over. */
    try {
      var f = JSON.parse(window.localStorage.getItem('fit-profile-v1') || 'null');
      if (f && f.height && f.weight) return { h: f.height, w: f.weight, b: { snella: 'slim', media: 'mid', morbida: 'full' }[f.shape] || 'mid', waist: f.waist, hip: f.hip };
    } catch (err) { /* idem */ }
    return {};
  }

  /* Thema 2 (03-10-2026): the size panels on product cards and in the cart rail ask for her answers too
     (assets/theme.js, advice on the plus). */
  MC.profile = readProfile;

  function saveProfile(p) {
    try { window.localStorage.setItem(KEY, JSON.stringify(p)); } catch (err) { /* private mode: advice still shows */ }
  }

  function clearProfile() {
    try {
      window.localStorage.removeItem(KEY);
      window.localStorage.removeItem('fit-profile-v1');
    } catch (err) { /* idem */ }
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function fill(text, values) {
    return String(text || '').replace(/%(\w+)%/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  /* Thema 2: the line under the sizes is a card with an icon, so shoppers see the advice (owner 29-09-2026). */
  var ICONS = {
    ruler: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="8" rx="1.5"/><path d="M7 8v3M11 8v4M15 8v3"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'
  };
  function icon(name) {
    var span = el('span', 'mc-hint__icon', '');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = ICONS[name] || '';
    return span;
  }

  function fmt(n, decimals, sep) {
    var s = String(Math.round(n * Math.pow(10, decimals)) / Math.pow(10, decimals));
    return sep === ',' ? s.replace('.', ',') : s;
  }

  function start() {
    var box = document.querySelector('[data-mc]');
    if (!box) return;
    var data;
    var S;
    try {
      data = JSON.parse(box.querySelector('script[data-mc-product]').textContent);
      S = JSON.parse(box.querySelector('script[data-mc-strings]').textContent);
    } catch (err) {
      return;
    }
    /* The t filter hands over escaped text ("s&#39;affiche"); the script writes it as plain text, so decode once. */
    var decoder = document.createElement('textarea');
    Object.keys(S).forEach(function (k) {
      if (typeof S[k] !== 'string') return;
      decoder.innerHTML = S[k];
      S[k] = decoder.value;
    });
    MC.setBody(box.getAttribute('data-mc-body'));
    var a = MC.analyse(data);
    var hint = document.querySelector('[data-mc-hint]');
    if (!a.can) return;
    /* The colour option, when the pop-up was not told: the product section carries it. */
    if (!(data.colorIndex >= 0)) {
      var section = document.querySelector('[data-color-index]');
      data.colorIndex = section ? parseInt(section.getAttribute('data-color-index'), 10) : -1;
    }
    var shoe = a.kind === 'shoe';
    var sep = S.decimal || ',';
    box.hidden = false;
    /* Thema 2 (02-10-2026, owner: "als klanten drukken op die knop dan moet heel die tablad compact zijn die sizechart van
       elk product hoeft daaronder niet getoond te worden"): with the coach on, the pop-up shows the coach alone. Only when
       the coach can advise on this product; otherwise the chart stays, so the pop-up is never empty. */
    var dialog = box.closest('[data-size-guide]');
    if (dialog) dialog.classList.add('sg--coach');
    var groups = box.querySelectorAll('[data-mc-group]');
    for (var g = 0; g < groups.length; g++) groups[g].hidden = groups[g].getAttribute('data-mc-group') !== (shoe ? 'shoe' : 'body');
    var title = box.querySelector('[data-mc-title]');
    if (title && shoe) title.textContent = S.titleShoe;
    var intro = box.querySelector('[data-mc-intro]');
    if (intro && shoe) intro.textContent = S.introShoe;
    var result = box.querySelector('[data-mc-result]');
    var form = box.querySelector('[data-mc-form]');
    var group = box.querySelector('[data-mc-group="' + (shoe ? 'shoe' : 'body') + '"]');
    var profile = readProfile();

    /* Thema 2 (03-10-2026, owner: "de maatcoach die moet wel op de juiste kg of lbs zitten of qua cm afhankelijk van
       market"): the fields follow her market (snippets/maatcoach.liquid, data-mc-units: metric, lb for the United States
       and Canada, st for the United Kingdom), with a switch in those markets. Her answers are always stored in cm and kg,
       so the engine, the advice on every other page and the advice on the plus stay the same. */
    var marketUnits = box.getAttribute('data-mc-units') || 'metric';
    var unitPref = null;
    try { unitPref = window.localStorage.getItem('maatcoach-units'); } catch (err) { /* storage blocked: the market decides */ }
    var imperial = (unitPref || marketUnits) !== 'metric';
    var unitToggle = box.querySelector('[data-mc-units-toggle]');
    function showUnits() {
      var blocks = box.querySelectorAll('[data-mc-unit]');
      for (var i = 0; i < blocks.length; i++) blocks[i].hidden = (blocks[i].getAttribute('data-mc-unit') === 'imperial') !== imperial;
      if (unitToggle) unitToggle.textContent = unitToggle.getAttribute(imperial ? 'data-label-metric' : 'data-label-imperial');
    }
    function impField(k) { return group.querySelector('[data-mc-imp="' + k + '"]'); }
    function impValue(k) {
      var f = impField(k);
      if (!f || String(f.value).trim() === '') return null;
      var n = parseFloat(String(f.value).replace(',', '.'));
      return isFinite(n) && n >= 0 ? n : null;
    }
    function fillImperial() {
      function put(k, v) { var f = impField(k); if (f) f.value = v == null ? '' : v; }
      if (profile.h) {
        var total = profile.h / 2.54;
        var ft = Math.floor(total / 12);
        var inch = Math.round(total - ft * 12);
        if (inch === 12) { ft += 1; inch = 0; }
        put('ft', ft);
        put('in', inch);
      }
      if (profile.w) {
        var lbs = profile.w / 0.45359237;
        if (impField('st')) { var st = Math.floor(lbs / 14); put('st', st); put('lb', Math.round(lbs - st * 14)); }
        else put('lb', Math.round(lbs));
      }
      ['chest', 'waist', 'hip', 'foot'].forEach(function (k) { if (profile[k]) put(k + '_in', Math.round(profile[k] / 2.54 * 10) / 10); });
      put('shoe_us', shoeIn('us'));
    }
    function readImperial() {
      if (impField('ft')) {
        var ft = impValue('ft');
        var inch = impValue('in');
        if (ft != null || inch != null) profile.h = Math.round(((ft || 0) * 12 + (inch || 0)) * 2.54);
        else delete profile.h;
      }
      if (impField('lb')) {
        var st = impValue('st');
        var lb = impValue('lb');
        if (st != null || lb != null) profile.w = Math.round(((st || 0) * 14 + (lb || 0)) * 0.45359237 * 10) / 10;
        else delete profile.w;
      }
      ['chest', 'waist', 'hip', 'foot'].forEach(function (k) {
        if (!impField(k + '_in')) return;
        var v = impValue(k + '_in');
        if (v != null && v > 0) profile[k] = Math.round(v * 2.54 * 10) / 10;
        else delete profile[k];
      });
      /* Her usual shoe size in US sizes (the United States and Canada); stored with its system, the engine carries it
         over to a model in EU sizes. */
      if (impField('shoe_us')) {
        var us = impValue('shoe_us');
        if (us != null && us > 0) {
          profile.shoe = us;
          profile.shoeSys = 'us';
        } else {
          delete profile.shoe;
          delete profile.shoeSys;
        }
      }
    }

    /* Her usual shoe size in the system a select asks in ('' when she has none). */
    function shoeIn(sys) {
      var v = Number(profile.shoe);
      if (!(v > 0)) return '';
      return String(MC.shoeConvert(v, profile.shoeSys === 'us' ? 'us' : 'eu', sys));
    }

    /* Her usual size as she gave it ("US 7", "38"), or else the size her foot length usually is, in this model's system. */
    function usualText(res) {
      var u = res.given || { size: res.usual, sys: res.sys };
      return (u.sys === 'us' ? 'US ' : '') + fmt(u.size, 1, sep);
    }

    function sizeInput(size) {
      var wanted = String(size).trim().toLowerCase();
      var inputs = document.querySelectorAll('input[data-option-index="' + data.sizeIndex + '"]');
      for (var i = 0; i < inputs.length; i++) {
        if (String(inputs[i].value).trim().toLowerCase() === wanted && !inputs[i].closest('[data-option-mirror]')) return inputs[i];
      }
      var select = document.querySelector('select[data-option-index="' + data.sizeIndex + '"]');
      return select || null;
    }

    function currentColour() {
      if (!(data.colorIndex >= 0)) return null;
      var c = document.querySelector('input[data-option-index="' + data.colorIndex + '"]:checked') || document.querySelector('select[data-option-index="' + data.colorIndex + '"]');
      return c ? c.value : null;
    }

    /* Is her size in stock in the colour on screen, and in which colours it is. */
    function stock(size) {
      var v = data.variants || [];
      var colour = currentColour();
      var here = null;
      var colours = [];
      for (var i = 0; i < v.length; i++) {
        if (v[i].o[data.sizeIndex] !== size) continue;
        var c = data.colorIndex >= 0 ? v[i].o[data.colorIndex] : null;
        if (c === colour || c === null) here = here || v[i].a;
        if (c !== null && v[i].a && colours.indexOf(c) === -1) colours.push(c);
      }
      return { here: here !== false, colours: colours };
    }

    function fillInputs() {
      var fields = group.querySelectorAll('[data-mc-input]');
      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        var key = f.getAttribute('data-mc-input');
        if (f.type === 'radio') f.checked = (profile[key] || 'mid') === f.value;
        else if (key === 'shoe') f.value = shoeIn('eu');
        else if (profile[key] != null && profile[key] !== '') f.value = profile[key];
      }
      if (imperial) fillImperial();
      var own = group.querySelector('[data-mc-own]');
      if (own && (profile.chest || profile.waist || profile.hip || profile.foot)) own.open = true;
    }

    function readInputs() {
      var fields = group.querySelectorAll('[data-mc-input]');
      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        var key = f.getAttribute('data-mc-input');
        if (f.type === 'radio') {
          if (f.checked) profile[key] = f.value;
          continue;
        }
        if (imperial && f.closest('[data-mc-unit="metric"]')) continue;
        var n = parseFloat(String(f.value).replace(',', '.'));
        if (isFinite(n) && n > 0) profile[key] = n;
        else delete profile[key];
        if (key === 'shoe') {
          if (profile.shoe) profile.shoeSys = 'eu';
          else delete profile.shoeSys;
        }
      }
      if (imperial) readImperial();
    }

    function sizeWord(size) { return String(size); }

    function reasonLine(res) {
      if (shoe) {
        if (res.basis === 'foot') return fill(S.shoeFoot, { foot: fmt(res.foot, 1, sep), size: res.pick, col: fmt(res.col, 1, sep) });
        if (res.basis === 'footStd') return fill(S.shoeFootStd, { foot: fmt(res.foot, 1, sep), size: res.pick });
        if (res.runs === 'small') return fill(S.shoeSmall, { size: res.pick, usual: usualText(res) });
        if (res.half) return fill(S.shoeHalf, { usual: usualText(res), size: res.pick });
        if (res.range) return fill(S.shoeRange, { size: res.pick, usual: usualText(res) });
        if (res.runs === 'large') return fill(S.shoeLarge, { size: res.pick });
        return fill(a.shoe.footOk ? S.shoeUsual : S.shoeUsualOnly, { size: res.pick });
      }
      if (res.basis === 'standard') return fill(S.standard, { size: res.pick });
      if (a.kind === 'set') return res.split ? fill(S.setSplit, { top: res.split[0].size, bottom: res.split[1].size, size: res.pick }) : fill(S.set, { size: res.pick });
      if (a.kind === 'dress') return fill(S.dress, { size: res.pick });
      var cut = res.cut || 'regular';
      if (res.dim === 'chest') {
        if (cut === 'near' || res.ease == null || res.ease < 2) return fill(a.sub === 'knit' ? S.chestNearKnit : S.chestNear, { size: res.pick });
        return fill(S['chest_' + cut] || S.chest_regular, { size: res.pick, ease: res.ease });
      }
      if (cut === 'near' || res.ease == null || res.ease < 2) return fill(S.bottomNear, { size: res.pick });
      return fill(res.dim === 'hip' ? S.hipEase : S.waistEase, { size: res.pick, ease: res.ease });
    }

    function betweenLine(res) {
      var v = { lower: res.lower, upper: res.upper };
      if (res.prefer === 'down') return fill(S.betweenAmple, v);
      if (a.kind === 'bottom') return fill(S.betweenBottom, v);
      if (a.sub === 'outer') return fill(S.betweenOuter, v);
      return fill(S.betweenUp, v);
    }

    function renderResult(res) {
      if (!result) return;
      result.innerHTML = '';
      if (res.status === 'need') {
        result.appendChild(el('p', 'mc__wait', shoe ? S.waitShoe : S.wait));
        return;
      }
      var card = el('div', 'mc__answer', '');
      if (res.status === 'out') {
        var p = el('p', 'mc__note', fill(shoe ? S.outShoe : S.out, { size: res.pick }) + ' ');
        var link = el('a', '', shoe ? S.contactShoe : S.contact);
        link.href = S.contactUrl || '/pages/contact';
        p.appendChild(link);
        card.appendChild(p);
        result.appendChild(card);
        return;
      }
      card.appendChild(el('p', 'mc__label', S.label));
      var pick = el('p', 'mc__pick', '');
      pick.appendChild(el('span', 'mc__pick-size', sizeWord(res.pick)));
      card.appendChild(pick);
      card.appendChild(el('p', 'mc__line', reasonLine(res)));
      if (res.between) card.appendChild(el('p', 'mc__line', betweenLine(res)));
      if (shoe && res.tight) card.appendChild(el('p', 'mc__line', fill(S.shoeTight, { size: res.alt })));
      if (res.flags && res.flags.roomy) card.appendChild(el('p', 'mc__note', fill(shoe ? S.roomyShoe : S.roomy, { size: res.pick })));
      if (res.flags && res.flags.snug) card.appendChild(el('p', 'mc__note', fill(shoe ? S.snugShoe : S.snug, { size: res.pick })));
      /* Thema 2 (02-10-2026, owner: "Hou het simpel en clean"): no line about where the hem falls, no centimetres. */
      var st = stock(res.pick);
      if (!st.here) {
        var others = st.colours.filter(function (c) { return c !== currentColour(); });
        card.appendChild(el('p', 'mc__note', fill(others.length ? S.soldHere : S.soldAll, { size: res.pick, colours: others.join(', ') })));
      }
      if (sizeInput(res.pick) && st.here) {
        var b = el('button', 'btn btn--full mc__choose', fill(S.choose, { size: res.pick }));
        b.type = 'button';
        b.setAttribute('data-size-guide-choose', res.pick);
        card.appendChild(b);
      }
      if (res.alt && sizeInput(res.alt) && stock(res.alt).here) {
        var alt = el('button', 'mc__alt', fill(S.or, { size: res.alt }));
        alt.type = 'button';
        alt.setAttribute('data-size-guide-choose', res.alt);
        card.appendChild(alt);
      }
      var reassure = shoe ? S.reassureShoe : S.reassure;
      if (reassure) card.appendChild(el('p', 'mc__reassure', reassure));
      result.appendChild(card);
    }

    /* The line under the sizes and the mark on her size. */
    function renderHint(res) {
      var marked = document.querySelectorAll('.is-advised');
      for (var i = 0; i < marked.length; i++) {
        marked[i].classList.remove('is-advised');
        var sr = marked[i].querySelector('.mc-sr');
        if (sr) sr.parentNode.removeChild(sr);
      }
      if (!hint) return;
      hint.innerHTML = '';
      hint.classList.remove('mc-hint--ask', 'mc-hint--out', 'mc-hint--done');
      var open = el('button', 'mc-hint__open', '');
      open.type = 'button';
      open.setAttribute('data-size-guide-open', '');
      open.setAttribute('aria-haspopup', 'dialog');
      open.setAttribute('aria-controls', 'SizeGuideDialog');
      /* Compact (thema-instelling maatcoach_compact): één knop rechts naast de maat, zoals de maatgidsknop van Viveane.
         Vóór het invullen de naam van de maatgids, daarna het advies; de uitleg staat in het venster. */
      if (hint.hasAttribute('data-mc-compact')) {
        var advised = res.status !== 'need' && res.status !== 'out';
        hint.classList.add(advised ? 'mc-hint--done' : res.status === 'out' ? 'mc-hint--out' : 'mc-hint--ask');
        open.appendChild(icon(advised ? 'check' : 'ruler'));
        open.appendChild(el('span', '', advised ? fill(shoe ? S.hintForShoe : S.hintFor, { size: res.pick }) : (hint.getAttribute('data-mc-label') || (shoe ? S.hintFindShoe : S.hintFind))));
        hint.appendChild(open);
        hint.hidden = false;
        if (advised) markAdvised(res.pick);
        return;
      }
      if (res.status === 'need') {
        hint.classList.add('mc-hint--ask');
        hint.appendChild(icon('ruler'));
        /* Thema 2: the whole card is the button (compact, one line on a phone): its title is the call to action and the
           button itself lies over the card, so the words are read once, by the button. */
        var askTitle = el('span', 'mc-hint__ask', shoe ? S.hintFindShoe : S.hintFind);
        askTitle.setAttribute('aria-hidden', 'true');
        hint.appendChild(askTitle);
        var sub = shoe ? (S.hintSubShoe || S.hintSub) : S.hintSub;
        if (sub) hint.appendChild(el('span', 'mc-hint__sub', sub));
        open.textContent = shoe ? S.hintFindShoe : S.hintFind;
        hint.appendChild(open);
        hint.hidden = false;
        return;
      }
      if (res.status === 'out') {
        hint.classList.add('mc-hint--out');
        hint.appendChild(icon('ruler'));
        hint.appendChild(el('span', 'mc-hint__ask', shoe ? S.hintOutShoe : S.hintOut));
        open.textContent = S.hintSee;
        hint.appendChild(open);
        hint.hidden = false;
        return;
      }
      var st = stock(res.pick);
      hint.classList.add('mc-hint--done');
      hint.appendChild(icon('check'));
      hint.appendChild(el('strong', 'mc-hint__size', fill(shoe ? S.hintForShoe : S.hintFor, { size: res.pick })));
      if (!st.here) hint.appendChild(el('span', 'mc-hint__sold', S.hintSold));
      var basis;
      if (shoe) basis = res.basis === 'foot' || res.basis === 'footStd' ? fill(S.hintBasisFoot, { foot: fmt(res.foot, 1, sep) }) : fill(S.hintBasisShoe, { shoe: usualText(res) });
      else basis = res.own ? S.hintBasisOwn : fill(S.hintBasis, { height: Math.round(profile.h), weight: Math.round(profile.w) });
      hint.appendChild(el('span', 'mc-hint__basis', basis));
      open.textContent = S.hintEdit;
      hint.appendChild(open);
      hint.hidden = false;
      markAdvised(res.pick);
    }

    /* The mark on her size among the size buttons. */
    function markAdvised(pick) {
      var input = sizeInput(pick);
      if (input && input.type === 'radio') {
        var label = document.querySelector('label[for="' + input.id + '"]');
        if (label) {
          label.classList.add('is-advised');
          label.appendChild(el('span', 'mc-sr visually-hidden', ' (' + S.advised + ')'));
        }
      }
    }

    function refresh(showResult) {
      var res = MC.advise(a, profile);
      renderHint(res);
      if (showResult) renderResult(res);
      return res;
    }

    /* Thema 2: once she has answered, her size is chosen for her on every product page, unless she picked a size
       herself on this page or came in on a variant link (a Google Shopping ad names the variant it showed). */
    var picked = /[?&]variant=\d/.test(window.location.search);
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (event.isTrusted && t && t.matches && t.matches('input[data-option-index="' + data.sizeIndex + '"]')) picked = true;
    });
    function autoPick(res) {
      if (picked || !res || res.status !== 'ok' || !res.pick) return;
      var input = sizeInput(res.pick);
      if (!input || input.type !== 'radio' || input.checked || !stock(res.pick).here) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    showUnits();
    fillInputs();
    autoPick(refresh(true));
    if (unitToggle) {
      unitToggle.addEventListener('click', function () {
        imperial = !imperial;
        try { window.localStorage.setItem('maatcoach-units', imperial ? marketUnits : 'metric'); } catch (err) { /* idem */ }
        showUnits();
        fillInputs();
      });
    }

    var timer = null;
    function onInput() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        readInputs();
        profile.t = Date.now();
        saveProfile(profile);
        autoPick(refresh(true));
      }, 250);
    }
    if (form) {
      form.addEventListener('submit', function (event) { event.preventDefault(); onInput(); });
      form.addEventListener('input', onInput);
      form.addEventListener('change', onInput);
    }
    var reset = box.querySelector('[data-mc-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        clearProfile();
        profile = {};
        var fields = box.querySelectorAll('[data-mc-input]');
        for (var i = 0; i < fields.length; i++) {
          if (fields[i].type === 'radio') fields[i].checked = fields[i].value === 'mid';
          else fields[i].value = '';
        }
        refresh(true);
      });
    }
    /* A new colour can put her size out of stock, or back. */
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.matches && t.matches('[data-option-index="' + data.colorIndex + '"]')) autoPick(refresh(true));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
